import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Modal } from '@/components/ui/modal'
import { AvailableDevicePicker } from '@/components/ui/available-device-picker'
import { Breadcrumbs, DetailList, Panel } from '@/components/ui/detail-list'
import { apiClient } from '@/lib/api-client'
import { statusLabel } from '@/lib/device-format'
import { formatDate, getStatusColor } from '@shared/utils'
import { toast } from 'react-hot-toast'

function isCurrent(assignment: any) {
  return !assignment.returnedOn && (assignment.status === 'approved' || assignment.status === 'pending')
}

export function UserDetailPage() {
  const { userId } = useParams()
  const queryClient = useQueryClient()
  const [assignOpen, setAssignOpen] = useState(false)

  const { data: person, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => (await apiClient.get(`/users/${userId}`)).data,
    enabled: !!userId,
  })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!person) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm font-medium">User not found</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/users">Back to users</Link>
        </Button>
      </div>
    )
  }

  const assignments = [...(person.deviceAssignments || [])].sort((left: any, right: any) => {
    const rank = (item: any) => {
      if (!isCurrent(item)) return 2
      return item.status === 'pending' ? 0 : 1
    }
    const byRank = rank(left) - rank(right)
    if (byRank) return byRank
    const leftTime = new Date(left.returnedOn || left.assignedOn || 0).getTime()
    const rightTime = new Date(right.returnedOn || right.assignedOn || 0).getTime()
    return rightTime - leftTime
  })

  return (
    <div className="space-y-4">
      <Breadcrumbs items={[{ label: 'Users', to: '/users' }, { label: person.name }]} />

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Panel title="Person">
          <DetailList
            items={[
              { label: 'Name', value: person.name },
              { label: 'Email', value: person.email },
              { label: 'Employee ID', value: person.employeeId, mono: true },
              { label: 'Role', value: person.role },
              { label: 'Joined', value: formatDate(person.createdAt) },
            ]}
          />
        </Panel>

        <Panel
          title="Devices"
          actions={
            <Button size="sm" onClick={() => setAssignOpen(true)}>
              Assign device
            </Button>
          }
        >
          {assignments.length === 0 ? (
            <p className="px-4 py-10 text-sm text-muted-foreground">No devices have been assigned to this person.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Serial</th>
                    <th>Status</th>
                    <th>Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment: any) => {
                    const device = assignment.device
                    return (
                      <tr key={assignment.id}>
                        <td>
                          {device ? (
                            <Link to={`/devices/${device.id}`} className="font-medium text-primary hover:underline">
                              {device.deviceModel?.name || device.name}
                            </Link>
                          ) : '—'}
                        </td>
                        <td data-label="Serial" className="font-mono text-xs">{device?.serialNumber || '—'}</td>
                        <td data-label="Status">
                          <Badge className={getStatusColor(isCurrent(assignment) ? (device?.status || assignment.status) : 'returned')}>
                            {isCurrent(assignment) ? statusLabel(device?.status || assignment.status) : 'Returned'}
                          </Badge>
                        </td>
                        <td data-label="Assigned" className="text-muted-foreground">{formatDate(assignment.assignedOn)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <AssignDeviceModal
        isOpen={assignOpen}
        userId={person.id}
        userName={person.name}
        onClose={() => setAssignOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['user', userId] })
          queryClient.invalidateQueries({ queryKey: ['devices'] })
          queryClient.invalidateQueries({ queryKey: ['assignments'] })
        }}
      />
    </div>
  )
}

function AssignDeviceModal({
  isOpen,
  onClose,
  userId,
  userName,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  userId: number
  userName: string
  onSuccess: () => void
}) {
  const [deviceId, setDeviceId] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setDeviceId('')
    setNotes('')
  }, [isOpen])

  const submit = async () => {
    if (!deviceId) {
      toast.error('Choose an available device')
      return
    }
    setLoading(true)
    try {
      await apiClient.post('/assignments', {
        deviceId: Number(deviceId),
        employeeId: userId,
        notes: notes.trim() || undefined,
      })
      toast.success(`Device assigned to ${userName}`)
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not assign the device')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Assign to ${userName}`} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Only devices that are free and unassigned are listed.
        </p>
        <AvailableDevicePicker value={deviceId} onChange={setDeviceId} />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Optional note for this handover"
          rows={3}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={loading}>
            {loading ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
